/**
 * Herkent een client die een OUDERE build draait dan de server bedient.
 *
 * WAAROM DIT NAAST `staleDeployment.ts` STAAT. Dat bestand vangt de LUIDRUCHTIGE
 * variant: een openstaande pagina vraagt een chunk op die na een nieuwe deploy
 * niet meer bestaat, en dat geeft een laadfout.
 *
 * Op 01-09-2026 bleek er ook een STILLE variant. Cloudflare Pages blijft de
 * assets van eerdere deployments bedienen, dus een oude app-shell laadt gewoon
 * zijn eigen — nog bestaande — chunks. Er gaat niets stuk, er komt geen
 * foutmelding, en de gebruiker draait maandenlang een build die niemand meer
 * verwacht. Precies dat gebeurde onder portal.collectr.be, waar een oud Operator
 * App-artefact op het devproject bleef praten.
 *
 * DE VALKUIL DIE DIT ONTWERP VERMIJDT. Een verouderde bundel kan zijn eigen
 * actualiteit niet uit zichzelf afleiden: vergelijkt hij een ingebakken waarde
 * met een andere ingebakken waarde, dan vindt hij zichzelf altijd actueel. De
 * vergelijking loopt daarom tussen twee ONAFHANKELIJKE bronnen:
 *
 *   ingebakken `__BUILD_ID__`  ↔  `build-meta.json`, opgehaald met `no-store`
 *
 * De eerdere versie las de `<script type=module src>` uit het levende DOM en
 * vergeleek die met een verse `/`. Dat wás een externe vergelijking, maar zij
 * hing aan het parsen van HTML met een reguliere expressie én zij blokkeerde
 * niets: de gebruiker kreeg een wegklikbare balk. Beide bezwaren zijn hier weg.
 */

/** Door Vite ingebakken bij het bouwen (`define` in vite.config.ts). */
declare const __BUILD_ID__: string | undefined;

export const HERLAAD_TELLER = "collectr:buildid-herladen";
/**
 * Hoogstens twee automatische herladingen. Eén is te krap wanneer de eerste
 * herlading zelf nog een verouderd document uit de cache krijgt; daarboven is
 * het een lus en moet de gebruiker het heft in handen krijgen.
 */
export const MAX_HERLADINGEN = 2;

export type BuildVergelijking =
  | { soort: "actueel"; buildId: string }
  | { soort: "verouderd"; draaiend: string; actueel: string }
  /** Niet vast te stellen: offline, geen metadata, geen ingebakken id. */
  | { soort: "onbepaald"; reden: string };

export type Herlaadactie = "niets" | "herladen" | "blokkeren";

/** `sessionStorage`-vorm, injecteerbaar zodat de teller toetsbaar is zonder browser. */
export interface HerlaadOpslag {
  getItem(sleutel: string): string | null;
  setItem(sleutel: string, waarde: string): void;
  removeItem(sleutel: string): void;
}

/** De build-id die IN deze bundel is gebakken. */
export function draaiendeBuildId(): string | null {
  const id = typeof __BUILD_ID__ === "string" ? __BUILD_ID__.trim() : "";
  return id.length > 0 ? id : null;
}

/** Zuivere vergelijking; alle IO zit erbuiten. */
export function vergelijkBuildIds(
  draaiend: string | null | undefined,
  actueel: string | null | undefined,
): BuildVergelijking {
  const a = draaiend?.trim() ?? "";
  const b = actueel?.trim() ?? "";
  if (a.length === 0) return { soort: "onbepaald", reden: "geen ingebakken build-id" };
  if (b.length === 0) return { soort: "onbepaald", reden: "geen actuele build-id" };
  return a === b ? { soort: "actueel", buildId: a } : { soort: "verouderd", draaiend: a, actueel: b };
}

/** Leest de build-id uit de inhoud van `build-meta.json`. Vorm afwijkend ⇒ null. */
export function buildIdUitMeta(ruw: unknown): string | null {
  if (typeof ruw !== "object" || ruw === null) return null;
  const waarde = (ruw as { buildId?: unknown }).buildId;
  return typeof waarde === "string" && waarde.trim().length > 0 ? waarde.trim() : null;
}

/**
 * Wat doen we met deze uitkomst?
 *
 * - `actueel` en `onbepaald` ⇒ niets. Offline zijn of een ontbrekend
 *   metabestand is GEEN bewijs van veroudering, en een onterechte blokkade zou
 *   een werkende gebruiker buitensluiten. Dat is het expliciete veilige gedrag.
 * - `verouderd` ⇒ gecontroleerd herladen, hoogstens `max` keer.
 * - `verouderd` terwijl het herladen al `max` keer niet hielp ⇒ blokkeren en de
 *   gebruiker zelf laten herladen. Doorgaan met een build die aantoonbaar niet
 *   meer klopt is precies wat dit moet voorkomen.
 */
export function bepaalHerlaadactie(
  uitkomst: BuildVergelijking,
  pogingen: number,
  max: number = MAX_HERLADINGEN,
): Herlaadactie {
  if (uitkomst.soort !== "verouderd") return "niets";
  return pogingen < max ? "herladen" : "blokkeren";
}

export function leesPogingen(opslag: HerlaadOpslag, sleutel = HERLAAD_TELLER): number {
  const ruw = Number.parseInt(opslag.getItem(sleutel) ?? "0", 10);
  return Number.isFinite(ruw) && ruw > 0 ? ruw : 0;
}

export function verhoogPogingen(opslag: HerlaadOpslag, sleutel = HERLAAD_TELLER): number {
  const volgende = leesPogingen(opslag, sleutel) + 1;
  opslag.setItem(sleutel, String(volgende));
  return volgende;
}

/** Na een geslaagde controle mag de teller weg — anders blokkeert de volgende deploy meteen. */
export function wisPogingen(opslag: HerlaadOpslag, sleutel = HERLAAD_TELLER): void {
  opslag.removeItem(sleutel);
}

/**
 * Haalt de ACTUELE build-id op uit een aparte, niet-gecachete bron.
 *
 * `cache: "no-store"` én een cache-buster: het eerste instrueert de browser, het
 * tweede overleeft een tussenliggende cache die zich daar niets van aantrekt.
 */
export async function haalActueleBuildId(ophalen: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await ophalen(`/build-meta.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    return buildIdUitMeta(await res.json());
  } catch {
    return null;
  }
}

/**
 * Volledige controle: vergelijkt en zegt wat er moet gebeuren.
 *
 * `draaiend` is injecteerbaar zodat het samenspel toetsbaar is zonder een echte
 * build — in een testomgeving is `__BUILD_ID__` immers niet ingebakken.
 */
export async function controleerBuildIdentiteit(
  opslag: HerlaadOpslag,
  ophalen: typeof fetch = fetch,
  draaiend: string | null = draaiendeBuildId(),
): Promise<{ uitkomst: BuildVergelijking; actie: Herlaadactie }> {
  const uitkomst = vergelijkBuildIds(draaiend, await haalActueleBuildId(ophalen));
  if (uitkomst.soort === "actueel") wisPogingen(opslag);
  return { uitkomst, actie: bepaalHerlaadactie(uitkomst, leesPogingen(opslag)) };
}
